import React, { useState } from 'react'
import { makeStyles } from '@mui/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useMediaQuery } from 'react-responsive'
import InfoIcon from '@mui/icons-material/Info'
import styled from '@emotion/styled'

import { getIsUrlAYoutubeVideo } from '@/utils'
import { mediaQueryForMobiles, queryForMobiles } from '@/media-queries'

import VideoPlayer from '@/components/video-player'
import Button from '@/components/button'
import Link from '../link'
import Tooltip from '../tooltip'
import { routes } from '@/routes'
import { Close as CloseIcon } from '@/icons'
import { YouTubePlaceholder } from '../youtube-player'

const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    [mediaQueryForMobiles]: {
      height: '',
    },
  },
  image: {
    minHeight: '224px', // 3x 16/9 scaled down
    maxWidth: '33.3%',
    margin: '0.5rem',
    padding: 0,
    cursor: 'pointer',
    transition: 'all 100ms',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '& img': {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '400px',
      objectFit: 'contain', // (default: fill) required to fix stretched images on Edge
    },
  },
  '& .image': {
    transform: 'scale(1.05)',
  },
  expanded: {
    height: 'auto',
    '& .image': {
      maxWidth: '100%',
      maxHeight: '100dvh',
    },
    '& .image img': {
      width: 'auto',
      maxHeight: 'inherit',
    },
    '& .image:hover': {
      transform: false,
    },
    [mediaQueryForMobiles]: {
      height: 'auto',
    },
  },
  btn: {
    fontSize: '2rem',
    opacity: 0,
    transition: 'all 100ms',
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      fontSize: '100%',
    },
    [mediaQueryForMobiles]: {
      position: 'absolute',
      background: 'rgba(0, 0, 0, 1)',
    },
  },
  visible: {
    opacity: 0.5,
    '&:hover': {
      cursor: 'pointer',
      opacity: 1,
    },
  },
  btnLeft: {
    left: 0,
  },
  btnRight: {
    right: 0,
  },
  caption: {
    marginTop: '0.5rem',
  },
  videoPlayerWrapper: {
    width: '100%',
  },
  videoPlayerControls: {
    marginTop: '0.25rem',
    display: 'flex',
    justifyContent: 'right',
  },
  youtube: {
    width: 'calc(100vw - 20rem)',
    [mediaQueryForMobiles]: {
      width: 'auto',
    },
  },
  placeholder: {
    width: '224px',
    height: '224px', // 3x 16/9 scaled down
    backgroundColor: '#1e1e1e',
  },
  hidden: {
    display: 'none',
  },
  infoIcon: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    zIndex: 100,
    padding: '0.25rem',
  },
})

export const Image = ({
  image,
  onClick,
  isSelected,
  isExpanded,
}: {
  image: ImageDetails
  onClick?: () => void
  isSelected?: boolean
  isExpanded?: boolean
}) => {
  const classes = useStyles()
  const isMobile = useMediaQuery({ query: queryForMobiles })
  const isYoutube = image.url ? getIsUrlAYoutubeVideo(image.url) : false

  return (
    <div
      className={`${classes.image} ${
        isExpanded && isYoutube ? classes.youtube : ''
      } ${isExpanded && !isSelected ? classes.hidden : ''} image`}>
      {isExpanded && isYoutube ? (
        <div className={classes.videoPlayerWrapper}>
          <VideoPlayer url={image.url!} autoplay width="100%" height="500px" />
          {isMobile ? null : onClick ? (
            <div className={classes.videoPlayerControls}>
              <Button
                onClick={() => onClick()}
                color="secondary"
                icon={<CloseIcon />}>
                Close Player
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {!isYoutube || image.thumbnailUrl ? (
            <img
              src={image.thumbnailUrl || image.url || undefined}
              alt={image.alt || 'Image in gallery'}
              onClick={onClick}
            />
          ) : (
            <YouTubePlaceholder onClick={onClick} />
          )}
        </>
      )}
      {image.caption ? (
        <div className={classes.caption}>{image.caption}</div>
      ) : null}
      {image.id ? (
        <Tooltip title="View attachment">
          <Link
            className={classes.infoIcon}
            to={routes.viewAttachmentWithVar.replace(
              ':attachmentId',
              image.id
            )}>
            <InfoIcon />
          </Link>
        </Tooltip>
      ) : null}
    </div>
  )
}

const LoadingPlaceholders = ({ count }: { count: number }) => {
  const classes = useStyles()
  const shimmers = []

  for (let i = 0; i < count; i++) {
    shimmers.push(
      <div
        key={i}
        className={`${classes.image} ${classes.placeholder} image`}></div>
    )
  }

  return <>{shimmers}</>
}

export interface ImageDetails {
  id?: string
  alt?: string // alt
  caption?: string | React.ReactElement
  url: string | null // null if image needs background converting
  thumbnailUrl?: string
}

const ImageGallery = ({
  startSelected,
  images,
  onClickImage,
  onMoveNext,
  onMovePrev,
  showLoadingCount,
}: {
  startSelected?: boolean
  images?: ImageDetails[]
  onClickImage?: (image: ImageDetails) => void
  onMoveNext?: () => void
  onMovePrev?: () => void
  showLoadingCount?: number
}) => {
  const classes = useStyles()
  const isMobile = useMediaQuery({ query: queryForMobiles })
  const [selectedIdx, setSelectedIdx] = useState<null | number>(
    isMobile || startSelected ? 0 : null
  )

  return (
    <div
      className={`${classes.root} ${
        selectedIdx !== null ? classes.expanded : ''
      }`}>
      {showLoadingCount ? (
        <LoadingPlaceholders count={showLoadingCount} />
      ) : (
        <>
          <div
            className={`${classes.btn} ${classes.btnLeft} ${
              selectedIdx !== null && selectedIdx > 0 ? classes.visible : ''
            }`}
            onClick={() => {
              setSelectedIdx((currentVal) => {
                if (currentVal !== null && currentVal > 0) {
                  return currentVal - 1
                }
                return currentVal
              })

              if (onMovePrev) {
                onMovePrev()
              }
            }}>
            <ChevronLeftIcon />
          </div>
          {images
            ? images.map((image, i) => (
                <Image
                  key={image.id || image.url}
                  image={image}
                  isSelected={selectedIdx === i}
                  isExpanded={selectedIdx !== null}
                  onClick={() => {
                    setSelectedIdx((currentVal) => {
                      if (currentVal === i) {
                        return null
                      }
                      return i
                    })

                    if (onClickImage) {
                      onClickImage(image)
                    }
                  }}
                />
              ))
            : null}
          <div
            className={`${classes.btn} ${classes.btnRight} ${
              selectedIdx !== null && images && selectedIdx < images.length - 1
                ? classes.visible
                : ''
            }`}
            onClick={() => {
              setSelectedIdx((currentVal) => {
                if (
                  currentVal !== null &&
                  images &&
                  currentVal < images.length - 1
                ) {
                  return currentVal + 1
                }
                return currentVal
              })

              if (onMoveNext) {
                onMoveNext()
              }
            }}>
            <ChevronRightIcon />
          </div>
        </>
      )}
    </div>
  )
}

export default ImageGallery
